import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Palette, Target, Inbox, CreditCard, Settings as SettingsIcon, LogOut, Menu, X, ShieldCheck } from "lucide-react";
import Logo from "./Logo";
import PlanBadge from "./PlanBadge";
import { useAuth } from "../lib/auth";

const items = [
  { to: "/dashboard/profile", label: "Dashboard", icon: LayoutDashboard, key: "profile" },
  { to: "/dashboard/leads", label: "Lead Engine", icon: Target, key: "leads" },
  { to: "/dashboard/creative", label: "Creative Maker", icon: Palette, key: "creative" },
  { to: "/dashboard/messages", label: "Smart Inbox", icon: Inbox, key: "messages" },
  { to: "/dashboard/plans", label: "Plans & Billing", icon: CreditCard, key: "plans" },
  { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon, key: "settings" },
];

const adminItem = { to: "/dashboard/admin", label: "Admin", icon: ShieldCheck, key: "admin" };

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const visibleItems = user?.is_admin ? [...items, adminItem] : items;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
      isActive
        ? "bg-[#1D6AFF]/10 text-[#1D6AFF] border border-[#1D6AFF]/25 shadow-[0_4px_16px_-6px_rgba(29,106,255,0.25)]"
        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-transparent"
    }`;

  return (
    <>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 bg-white/95 backdrop-blur-xl border-b border-slate-200">
        <Logo size="sm" />
        <button
          data-testid="sidebar-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-100 border border-slate-200 text-slate-700"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <aside
        data-testid="dashboard-sidebar"
        className={`fixed top-0 left-0 h-screen w-72 bg-white border-r border-slate-200 z-30 transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } flex flex-col`}
      >
        <div className="px-6 pt-6 pb-8">
          <Logo size="md" />
        </div>

        <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto">
          {visibleItems.map((it) => (
            <NavLink
              key={it.key}
              to={it.to}
              data-testid={`sidebar-link-${it.key}`}
              className={linkClass}
              onClick={() => setOpen(false)}
            >
              <it.icon className="w-5 h-5 shrink-0" />
              <span className="font-medium">{it.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1D6AFF] to-[#0B3FA3] flex items-center justify-center text-white text-sm font-semibold">
              {(user?.name || "A").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate">{user?.name || "Agent"}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          <div className="px-2 mb-3">
            <PlanBadge plan={user?.plan || "free"} size="md" />
          </div>
          <button
            data-testid="sidebar-logout"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-slate-200 transition-all text-sm"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </aside>

      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-20"
          onClick={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
